if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        /* ── BUNDLE INTERCEPT ─────────────────────────────────
           If this product-form is a bundle, hand off to the
           bundle-specific add-to-cart logic instead of Dawn's
           normal single-item flow.
           ──────────────────────────────────────────────────── */
        if (this.dataset.isBundle === 'true') {
          this.handleBundleSubmit();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /* ── BUNDLE ADD-TO-CART ──────────────────────────────────
         Posts a multi-item payload to /cart/add.js:
         1 × parent (The GOAP Bundle, with Scent 1–4 properties)
         + 1 × child per unique scent variant (0-price shadow
           products that track inventory natively).
         ────────────────────────────────────────────────────── */
      async handleBundleSubmit() {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        try {
          const parentVariantId = this.form.querySelector('input[name="id"]').value;
          const qtyInput = this.form.querySelector('input[name="quantity"]');
          const bundleQty = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);

          /* Collect scent selections from dropdowns */
          const dropdowns = this.form.querySelectorAll('.custom-scent-dropdown');
          const componentCounts = {};   /* variantId → qty per single bundle */
          const bundleProps = {};

          for (const dropdown of dropdowns) {
            const variantId = dropdown.dataset.selectedVariantId;
            const titleEl = dropdown.querySelector('.selectedTitle');
            const title = titleEl ? titleEl.textContent.trim() : '';
            const propName = dropdown.getAttribute('data-property');

            if (!variantId || !title) {
              this.handleErrorMessage('Please select an option for ' + propName + '.');
              return;
            }
            bundleProps[propName] = title;
            componentCounts[variantId] = (componentCounts[variantId] || 0) + 1;
          }

          /* Inventory pre-check (client-side, from data attributes) */
          const inventoryLookup = {};
          dropdowns.forEach(function (d) {
            d.querySelectorAll('.scent-option').forEach(function (opt) {
              if (opt.dataset.variantId) {
                inventoryLookup[opt.dataset.variantId] = parseInt(opt.dataset.inventory, 10) || 0;
              }
            });
          });

          for (const [vid, perBundleQty] of Object.entries(componentCounts)) {
            const needed = perBundleQty * bundleQty;
            const available = inventoryLookup[vid] || 0;
            if (needed > available) {
              this.handleErrorMessage('Not enough stock for one of the selected scents. Please pick a different scent or reduce quantity.');
              return;
            }
          }

          /* Unique bundle id links parent + children */
          const bundleId = 'bnd-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);

          bundleProps['_bundle_id'] = bundleId;
          bundleProps['_bundle_parent'] = 'true';

          const items = [{
            id: parseInt(parentVariantId, 10),
            quantity: bundleQty,
            properties: bundleProps
          }];

          /* One child line per unique scent variant */
          for (const [vid, perBundleQty] of Object.entries(componentCounts)) {
            items.push({
              id: parseInt(vid, 10),
              quantity: perBundleQty * bundleQty,
              properties: {
                '_bundle_id': bundleId,
                '_bundle_child': 'true',
                '_bundle_child_per': String(perBundleQty)
              }
            });
          }

          /* POST multi-item add */
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: items })
          });
          const json = await res.json();

          if (!res.ok || json.status) {
            this.handleErrorMessage(json.description || json.message || 'Could not add bundle to cart.');
            return;
          }

          /* Refresh & open cart drawer */
          try {
            const sectionHtml = await fetch(window.location.pathname + '?section_id=cart-drawer').then(r => r.text());
            const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const freshDrawer = doc.querySelector('cart-drawer');
            const currentDrawer = document.querySelector('cart-drawer');
            if (freshDrawer && currentDrawer) {
              currentDrawer.innerHTML = freshDrawer.innerHTML;
              currentDrawer.classList.remove('is-empty');
            }
          } catch (_) { /* ignore refresh errors */ }

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          } else if (drawer) {
            drawer.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }

          /* Notify subscribers */
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: parentVariantId
            });
          }

        } catch (err) {
          console.error('[bundle-add] error', err);
          this.handleErrorMessage('Something went wrong. Please try again.');
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}